import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { useGetOrdersWithSelfDeclarationsQuery } from '../../core/redux/transport';
import { DeclarationsTable } from './partials/DeclarationsTable';
import { Order } from '../../core/types/transport/order';
import { Project } from '../../core/types/transport/project';
import { PageUnauthenticated } from '../../shared/components/page';
import { Header } from '../../components/header';
import { CustomDatePicker } from './partials/CustomDatePicker';
import './style.scss';

export interface FilterItem {
  value: string | number;
  label: string;
}

export const DeclarationsPage = () => {
  const { data, isLoading } = useGetOrdersWithSelfDeclarationsQuery();
  const [projectFilter, setProjectFilter] = useState<FilterItem[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<FilterItem[]>([]);
  const [fromDateFilter, setFromDateFilter] = useState<Date | null>(null);
  const [toDateFilter, setToDateFilter] = useState<Date | null>(null);
  const [search, setSearch] = useState<string>('');

  const createdBySet = useMemo(
    () => new Set(createdByFilter.map((f) => f.value)),
    [createdByFilter],
  );

  const projectSet = useMemo(() => new Set(projectFilter.map((f) => f.value)), [projectFilter]);

  const matchesCreatedBy = (order: Order) => createdBySet.size === 0
    || createdBySet.has(order.createdByName);

  const matchesProject = (order: Order) => projectSet.size === 0
    || (order.fromProject && projectSet.has(order.fromProject.id));

  const matchesFromDate = (order: Order) => fromDateFilter === null
    || new Date(order.createdTimestamp) >= fromDateFilter;

  const matchesToDate = (order: Order) => toDateFilter === null
    || new Date(order.createdTimestamp) <= toDateFilter;

  const matchesSearch = ({ fromProject, createdByName, contactPersonName }: Order) => {
    const text = search.toLowerCase();

    const projectMatch = fromProject && fromProject.projectName.toLowerCase().includes(text);
    const projectIdMatch = fromProject && fromProject.id.toString().includes(text);

    const createdByMatch = createdByName.toLowerCase().includes(text);
    const contactMatch = contactPersonName && contactPersonName.toLowerCase().includes(text);

    return text === '' || projectMatch || projectIdMatch || createdByMatch || contactMatch;
  };

  const filtered = useMemo(() => {
    if (!data) return [];

    return data.filter(
      (o) => matchesProject(o)
        && matchesCreatedBy(o)
        && matchesFromDate(o)
        && matchesToDate(o)
        && matchesSearch(o),
    );
  }, [data, projectFilter, createdByFilter, fromDateFilter, toDateFilter, search]);

  const projects = useMemo(() => {
    const map = new Map<number, Project>();
    data?.forEach((o) => {
      if (o.fromProject) {
        map.set(o.fromProject.id, o.fromProject);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  }, [data]);

  const orderers = useMemo(() => {
    const set = new Set<string>();
    data?.forEach((o) => set.add(o.createdByName));
    return Array.from(set).sort();
  }, [data]);

  return (
    <PageUnauthenticated className="egenerklaring-page">
      <Header>Egenerklæringsoversikt</Header>
      <Box display="flex" flex={1} flexDirection="column" gap={2} padding="1rem">
        <Box display="flex" justifyContent="space-between" gap={4}>
          <Autocomplete
            fullWidth
            multiple
            value={projectFilter}
            id="project"
            size="small"
            disableCloseOnSelect
            onChange={(e, ns) => setProjectFilter(ns)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            options={Array.from(projects).map((m) => ({
              value: m.id,
              label: `${m.id} - ${m.projectName}`,
            }))}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                label="Prosjekt"
                size="small"
              />
            )}
          />
          <Autocomplete
            fullWidth
            multiple
            value={createdByFilter}
            id="created-by"
            size="small"
            disableCloseOnSelect
            onChange={(e, ns) => setCreatedByFilter(ns)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            options={orderers.map((m) => ({ value: m, label: m }))}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                label="Innsender"
                size="small"
              />
            )}
          />
          <CustomDatePicker label="Fra Dato" value={fromDateFilter} onChange={setFromDateFilter} />
          <CustomDatePicker label="Til Dato" value={toDateFilter} onChange={setToDateFilter} />
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel htmlFor="rental-vehicle-search">Søk</InputLabel>
            <OutlinedInput
              id="rental-vehicle-search"
              value={search}
              fullWidth
              onChange={(e) => setSearch(e.target.value)}
              label="Søk"
              endAdornment={(
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Fjern søket"
                    onClick={() => setSearch('')}
                    onMouseDown={() => setSearch('')}
                    edge="end"
                  >
                    {search === '' ? null : <BackspaceIcon /> }
                  </IconButton>
                </InputAdornment>
            )}
            />
          </FormControl>
        </Box>
        <Box display="flex" flexDirection="column" gap="1rem">
          {isLoading && (
            <Box display="flex" justifyContent="center" mt={20}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && <DeclarationsTable data={filtered} />}
        </Box>
      </Box>
    </PageUnauthenticated>
  );
};
